import React, { useState, useEffect, useCallback } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './AssetCodeTool.css';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft,
  FaBuilding,
  FaShieldAlt,
  FaLayerGroup,
  FaSearch,
  FaCheckSquare,
  FaSquare,
  FaEdit,
  FaSave,
  FaTimes,
  FaUserCircle,
  FaSignOutAlt,
  FaBell,
  FaServer,
  FaHistory,
  FaCode,
  FaChartBar,
  FaDatabase,
  FaChevronDown,
  FaChevronRight,
  FaCheck,
  FaExclamationTriangle,
  FaMinusSquare,
  FaInfoCircle,
  FaUndo,
  FaBars,
  FaTrash
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const AssetCodeTool = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');

  // Selection state
  const [buildings, setBuildings] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedAssetType, setSelectedAssetType] = useState(null);

  // Assets state
  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Edit state
  const [editMode, setEditMode] = useState(false);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveResults, setSaveResults] = useState(null);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Loading states
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [modulesLoading, setModulesLoading] = useState(true);

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadInitialData();
  }, [navigate]);

  const loadInitialData = async () => {
    try {
      // Load buildings and modules in parallel
      const [buildingsRes, modulesRes, profileRes] = await Promise.all([
        fetch(getApiEndpoint('/api/cloud-staff/buildings'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(getApiEndpoint('/api/cloud-staff/modules'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(getApiEndpoint('/api/users/profile'), {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (buildingsRes.ok) {
        const data = await buildingsRes.json();
        setBuildings(data);
      }

      if (modulesRes.ok) {
        const data = await modulesRes.json();
        setModules(data);
      }

      if (profileRes.ok) {
        const data = await profileRes.json();
        setUserName(`${data.firstName || data.first_name || ''} ${data.lastName || data.last_name || ''}`);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load data');
    } finally {
      setBuildingsLoading(false);
      setModulesLoading(false);
    }
  };

  const loadAssets = useCallback(async () => {
    if (!selectedBuilding || !selectedModule || !selectedAssetType) return;

    setAssetsLoading(true);
    setAssets([]);
    setSelectedAssets(new Set());
    setEditMode(false);
    setEditValues({});
    setSaveResults(null);

    try {
      const response = await fetch(
        getApiEndpoint(`/api/cloud-staff/assets?property_id=${selectedBuilding.id}&module_key=${selectedModule.key}&asset_type_key=${selectedAssetType.key}`),
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setAssets(data);
      } else {
        toast.error('Failed to load assets');
      }
    } catch (error) {
      console.error('Error loading assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setAssetsLoading(false);
    }
  }, [selectedBuilding, selectedModule, selectedAssetType]);

  useEffect(() => {
    if (selectedBuilding && selectedModule && selectedAssetType) {
      loadAssets();
    }
  }, [selectedBuilding, selectedModule, selectedAssetType, loadAssets]);

  // Selection handlers
  const handleBuildingSelect = (building) => {
    setSelectedBuilding(building);
    setAssets([]);
    setSelectedAssets(new Set());
    setEditMode(false);
    setSaveResults(null);
  };

  const handleModuleSelect = (mod) => {
    setSelectedModule(mod);
    setSelectedAssetType(null);
    setAssets([]);
    setSelectedAssets(new Set());
    setEditMode(false);
    setSaveResults(null);
  };

  const handleAssetTypeSelect = (assetType) => {
    setSelectedAssetType(assetType);
  };

  // Multi-select handlers
  const toggleAssetSelection = (assetId) => {
    setSelectedAssets(prev => {
      const next = new Set(prev);
      if (next.has(assetId)) {
        next.delete(assetId);
      } else {
        next.add(assetId);
      }
      return next;
    });
  };

  const selectAll = () => {
    const filtered = getFilteredAssets();
    if (selectedAssets.size === filtered.length) {
      setSelectedAssets(new Set());
    } else {
      setSelectedAssets(new Set(filtered.map(a => a.id)));
    }
  };

  const getFilteredAssets = () => {
    if (!searchTerm) return assets;
    const term = searchTerm.toLowerCase();
    return assets.filter(a =>
      (a.name && a.name.toLowerCase().includes(term)) ||
      (a.reference_id && a.reference_id.toLowerCase().includes(term)) ||
      String(a.id).includes(term)
    );
  };

  // Edit mode handlers
  const enterEditMode = () => {
    if (selectedAssets.size === 0) {
      toast.warning('Please select at least one asset');
      return;
    }
    const initialValues = {};
    selectedAssets.forEach(id => {
      const asset = assets.find(a => a.id === id);
      if (asset) {
        initialValues[id] = {
          reference_id: asset.reference_id || '',
          external_ref: asset.external_ref || ''
        };
      }
    });
    setEditValues(initialValues);
    setEditMode(true);
    setSaveResults(null);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditValues({});
    setSaveResults(null);
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/assets/delete-selected'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          property_id: selectedBuilding?.id ?? selectedBuilding,
          module_key: selectedModule?.key ?? selectedModule,
          asset_type_key: selectedAssetType?.key ?? selectedAssetType,
          asset_ids: Array.from(selectedAssets)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Delete failed');
      toast.success(`Deleted ${data.deleted?.length || 0} asset(s)`);
      setAssets(prev => prev.filter(a => !selectedAssets.has(a.id)));
      setSelectedAssets(new Set());
      setShowDeleteConfirm(false);
    } catch (e) {
      toast.error(e.message || 'Failed to delete assets');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditChange = (assetId, field, value) => {
    setEditValues(prev => ({
      ...prev,
      [assetId]: {
        ...prev[assetId],
        [field]: value
      }
    }));
  };

  // Apply same value to all selected assets
  const applyToAll = (field, value) => {
    const updated = { ...editValues };
    selectedAssets.forEach(id => {
      if (updated[id]) {
        updated[id] = { ...updated[id], [field]: value };
      }
    });
    setEditValues(updated);
  };

  // Save handler
  const handleSave = async () => {
    setSaving(true);
    setSaveResults(null);

    try {
      const updates = Object.entries(editValues).map(([assetId, values]) => ({
        asset_id: parseInt(assetId),
        reference_id: values.reference_id || undefined,
        external_ref: values.external_ref || undefined
      }));

      const response = await fetch(getApiEndpoint('/api/cloud-staff/assets/update-references'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          property_id: selectedBuilding.id,
          module_key: selectedModule.key,
          asset_type_key: selectedAssetType.key,
          updates
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSaveResults(result);

        if (result.total_updated > 0) {
          toast.success(`Updated ${result.total_updated} asset(s) successfully`);
          // Refresh assets
          loadAssets();
          setEditMode(false);
          setEditValues({});
          setSelectedAssets(new Set());
        }

        if (result.total_errors > 0) {
          toast.warning(`${result.total_errors} asset(s) had errors`);
        }
      } else {
        const err = await response.json();
        toast.error(err.message || 'Failed to save changes');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const filteredAssets = getFilteredAssets();

  return (
    <div className="act-page">
      {/* Header */}
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge">
            <FaServer />
            <span>Cloud Staff Portal</span>
          </div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn" title="Notifications">
              <FaBell />
            </button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header">
                    <FaUserCircle className="cs-menu-avatar" />
                    <div>
                      <p className="cs-menu-name">{userName || 'Cloud Staff'}</p>
                      <p className="cs-menu-role">Cloud Staff Admin</p>
                    </div>
                  </div>
                  <div className="cs-user-menu-divider" />
                  <Link to="/cloud-staff/dashboard" className="cs-menu-item">
                    <FaChartBar /> Dashboard
                  </Link>
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout">
                    <FaSignOutAlt /> Sign Out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="cs-layout">
        <CloudStaffSidebar active="asset-code-tool" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />

        {/* Main Content */}
        <main className="act-content">
          {/* Page Header */}
          <div className="act-header">
            <div className="act-header-left">
              <Link to="/cloud-staff/dashboard" className="act-back-btn">
                <FaArrowLeft />
              </Link>
              <div>
                <h1>Asset Code Tool</h1>
                <p>Manage reference IDs and external references for building assets</p>
              </div>
            </div>
          </div>

          {/* Selection Panel */}
          <div className="act-selection-panel">
            {/* Step 1: Building */}
            <div className="act-step">
              <div className="act-step-header">
                <div className="act-step-number">1</div>
                <div className="act-step-info">
                  <h3><FaBuilding /> Select Building</h3>
                  {selectedBuilding && (
                    <span className="act-step-selected">{selectedBuilding.name}</span>
                  )}
                </div>
              </div>
              <div className="act-step-content">
                {buildingsLoading ? (
                  <div className="act-step-loading">Loading buildings...</div>
                ) : (
                  <div className="act-dropdown-list">
                    {buildings.map(building => (
                      <button
                        key={building.id}
                        className={`act-dropdown-item ${selectedBuilding?.id === building.id ? 'selected' : ''}`}
                        onClick={() => handleBuildingSelect(building)}
                      >
                        <FaBuilding />
                        <div className="act-dropdown-item-info">
                          <span className="act-dropdown-item-name">{building.name}</span>
                          <span className="act-dropdown-item-sub">{building.address}</span>
                        </div>
                        {selectedBuilding?.id === building.id && <FaCheck className="act-check" />}
                      </button>
                    ))}
                    {buildings.length === 0 && (
                      <div className="act-no-items">No buildings found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: Safety Module (Selection Type) */}
            <div className={`act-step ${!selectedBuilding ? 'disabled' : ''}`}>
              <div className="act-step-header">
                <div className="act-step-number">2</div>
                <div className="act-step-info">
                  <h3><FaShieldAlt /> Selection Type</h3>
                  {selectedModule && (
                    <span className="act-step-selected">{selectedModule.label}</span>
                  )}
                </div>
              </div>
              {selectedBuilding && (
                <div className="act-step-content">
                  {modulesLoading ? (
                    <div className="act-step-loading">Loading modules...</div>
                  ) : (
                    <div className="act-dropdown-list">
                      {modules.map(mod => (
                        <button
                          key={mod.key}
                          className={`act-dropdown-item ${selectedModule?.key === mod.key ? 'selected' : ''}`}
                          onClick={() => handleModuleSelect(mod)}
                        >
                          <FaShieldAlt />
                          <span className="act-dropdown-item-name">{mod.label}</span>
                          <span className="act-dropdown-item-count">
                            {mod.asset_types.length} types
                          </span>
                          {selectedModule?.key === mod.key && <FaCheck className="act-check" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Step 3: Category (Asset Type) */}
            <div className={`act-step ${!selectedModule ? 'disabled' : ''}`}>
              <div className="act-step-header">
                <div className="act-step-number">3</div>
                <div className="act-step-info">
                  <h3><FaLayerGroup /> Category</h3>
                  {selectedAssetType && (
                    <span className="act-step-selected">{selectedAssetType.label}</span>
                  )}
                </div>
              </div>
              {selectedModule && (
                <div className="act-step-content">
                  <div className="act-dropdown-list">
                    {selectedModule.asset_types.map(at => (
                      <button
                        key={at.key}
                        className={`act-dropdown-item ${selectedAssetType?.key === at.key ? 'selected' : ''}`}
                        onClick={() => handleAssetTypeSelect(at)}
                      >
                        <FaLayerGroup />
                        <span className="act-dropdown-item-name">{at.label}</span>
                        {selectedAssetType?.key === at.key && <FaCheck className="act-check" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Assets Table */}
          {selectedBuilding && selectedModule && selectedAssetType && (
            <div className="act-assets-section">
              <div className="act-assets-toolbar">
                <div className="act-toolbar-left">
                  <h2>
                    <FaDatabase /> {selectedAssetType.label}
                    <span className="act-count">{filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''}</span>
                  </h2>
                </div>
                <div className="act-toolbar-right">
                  <div className="act-search-box">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Search assets..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  {!editMode ? (
                    <div className="act-toolbar-btns">
                      <button
                        className="act-edit-btn"
                        onClick={enterEditMode}
                        disabled={selectedAssets.size === 0}
                      >
                        <FaEdit /> Edit Selected ({selectedAssets.size})
                      </button>
                      <button
                        className="act-delete-btn"
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={selectedAssets.size === 0}
                      >
                        <FaTrash /> Delete Selected ({selectedAssets.size})
                      </button>
                    </div>
                  ) : (
                    <div className="act-edit-actions">
                      <button className="act-cancel-btn" onClick={cancelEdit}>
                        <FaTimes /> Cancel
                      </button>
                      <button
                        className="act-save-btn"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? (
                          <>Saving...</>
                        ) : (
                          <><FaSave /> Save Changes</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Bulk apply bar (only in edit mode) */}
              {editMode && selectedAssets.size > 1 && (
                <div className="act-bulk-bar">
                  <FaInfoCircle />
                  <span>Apply to all {selectedAssets.size} selected assets:</span>
                  <div className="act-bulk-inputs">
                    <div className="act-bulk-field">
                      <label>Reference ID</label>
                      <div className="act-bulk-input-group">
                        <input
                          type="text"
                          placeholder="Enter reference ID for all"
                          id="bulk-ref-id"
                        />
                        <button
                          className="act-bulk-apply"
                          onClick={() => {
                            const val = document.getElementById('bulk-ref-id').value;
                            if (val) applyToAll('reference_id', val);
                          }}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Results */}
              {saveResults && (
                <div className={`act-results ${saveResults.total_errors > 0 ? 'has-errors' : 'success'}`}>
                  <div className="act-results-header">
                    {saveResults.total_errors > 0 ? (
                      <><FaExclamationTriangle /> Completed with {saveResults.total_errors} error(s)</>
                    ) : (
                      <><FaCheck /> Successfully updated {saveResults.total_updated} asset(s)</>
                    )}
                  </div>
                  {saveResults.errors && saveResults.errors.length > 0 && (
                    <div className="act-results-errors">
                      {saveResults.errors.map((err, i) => (
                        <div key={i} className="act-error-item">
                          Asset #{err.asset_id}: {err.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Table */}
              {assetsLoading ? (
                <div className="act-loading">
                  <div className="cs-spinner" />
                  <p>Loading assets...</p>
                </div>
              ) : filteredAssets.length > 0 ? (
                <div className="act-table-container">
                  <table className="act-table">
                    <thead>
                      <tr>
                        <th className="act-th-check">
                          <button className="act-select-all" onClick={selectAll}>
                            {selectedAssets.size === filteredAssets.length && filteredAssets.length > 0 ? (
                              <FaCheckSquare />
                            ) : selectedAssets.size > 0 ? (
                              <FaMinusSquare />
                            ) : (
                              <FaSquare />
                            )}
                          </button>
                        </th>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Reference ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAssets.map(asset => (
                        <tr
                          key={asset.id}
                          className={`${selectedAssets.has(asset.id) ? 'selected' : ''} ${editMode && selectedAssets.has(asset.id) ? 'editing' : ''}`}
                        >
                          <td className="act-td-check">
                            <button
                              className="act-check-btn"
                              onClick={() => toggleAssetSelection(asset.id)}
                            >
                              {selectedAssets.has(asset.id) ? <FaCheckSquare /> : <FaSquare />}
                            </button>
                          </td>
                          <td className="act-td-id">#{asset.id}</td>
                          <td className="act-td-name">{asset.name || 'Unnamed'}</td>
                          <td>
                            <span className="act-type-badge">{asset.type || 'N/A'}</span>
                          </td>
                          <td>
                            <span className={`act-status-badge ${(asset.status || 'unknown').toLowerCase()}`}>
                              {asset.status || 'Unknown'}
                            </span>
                          </td>
                          <td className="act-td-ref">
                            {editMode && selectedAssets.has(asset.id) ? (
                              <input
                                type="text"
                                className="act-edit-input"
                                value={editValues[asset.id]?.reference_id || ''}
                                onChange={(e) => handleEditChange(asset.id, 'reference_id', e.target.value)}
                                placeholder="Enter reference ID"
                              />
                            ) : (
                              <span className={`act-ref-value ${asset.reference_id ? '' : 'empty'}`}>
                                {asset.reference_id || '(not set)'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="act-empty">
                  <FaDatabase />
                  <p>
                    {assets.length === 0
                      ? 'No assets found for this building and category'
                      : 'No assets match your search'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* No selection prompt */}
          {(!selectedBuilding || !selectedModule || !selectedAssetType) && (
            <div className="act-prompt">
              <div className="act-prompt-icon">
                <FaDatabase />
              </div>
              <h2>Select a Building, Type, and Category</h2>
              <p>
                Use the selection panel above to choose a building, safety module, and asset
                category. Assets will be loaded automatically once all three selections are made.
              </p>
              <div className="act-prompt-steps">
                <div className={`act-prompt-step ${selectedBuilding ? 'done' : ''}`}>
                  <span className="act-prompt-step-num">{selectedBuilding ? <FaCheck /> : '1'}</span>
                  Select a Building
                </div>
                <FaChevronRight className="act-prompt-arrow" />
                <div className={`act-prompt-step ${selectedModule ? 'done' : ''}`}>
                  <span className="act-prompt-step-num">{selectedModule ? <FaCheck /> : '2'}</span>
                  Choose Selection Type
                </div>
                <FaChevronRight className="act-prompt-arrow" />
                <div className={`act-prompt-step ${selectedAssetType ? 'done' : ''}`}>
                  <span className="act-prompt-step-num">{selectedAssetType ? <FaCheck /> : '3'}</span>
                  Pick a Category
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* ── Delete Confirm Modal ── */}
      {showDeleteConfirm && (
        <div className="act-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="act-modal" onClick={e => e.stopPropagation()}>
            <div className="act-modal-header">
              <FaExclamationTriangle className="act-modal-warn-icon" />
              <h3>Delete {selectedAssets.size} Asset{selectedAssets.size !== 1 ? 's' : ''}?</h3>
              <button className="act-modal-close" onClick={() => setShowDeleteConfirm(false)}><FaTimes /></button>
            </div>
            <p className="act-modal-body">
              This will permanently delete {selectedAssets.size} selected asset{selectedAssets.size !== 1 ? 's' : ''} from the database. This action cannot be undone.
            </p>
            <div className="act-modal-footer">
              <button className="act-cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="act-confirm-delete-btn" onClick={handleDeleteSelected} disabled={deleting}>
                {deleting ? 'Deleting…' : <><FaTrash /> Delete {selectedAssets.size} Asset{selectedAssets.size !== 1 ? 's' : ''}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetCodeTool;
